import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { MicButton, appendText } from "@/components/MicButton";

export const AuthImg = ({ path, token, className }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(path.replace(/^\/api/, ""), { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path, token]);
  return src ? <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="" className={className} /></a> : <div className={`${className} animate-pulse`} />;
};

export const ShowcaseThumbs = ({ item }) => {
  const token = localStorage.getItem("rs_admin_token");
  return (
    <div className="flex gap-1" data-testid={`showcase-thumbs-${item.id}`}>
      {(item.photos || []).map((_, i) => (
        <AuthImg key={i} path={`/api/showcase/photos/${item.id}/${i}`} token={token} className="w-12 h-12 rounded object-cover bg-white hover:ring-2 ring-[#FF2E63]" />
      ))}
    </div>
  );
};

export const ShowcaseReview = ({ item, auth, onDone }) => {
  const set = async (status) => {
    try {
      await api.patch(`/admin/showcase/${item.id}`, { status }, auth);
      toast.success(status === "approved" ? "Approved — now live in the gallery" : "Rejected");
      onDone();
    } catch { toast.error("Could not update"); }
  };
  return (
    <div className="flex gap-1">
      {item.status !== "approved" && <Button size="sm" className="h-7 bg-emerald-500 hover:bg-emerald-400 text-[#0A192F] font-bold" onClick={() => set("approved")} data-testid={`showcase-approve-${item.id}`}><Check className="w-3 h-3 mr-1" />Approve</Button>}
      {item.status !== "rejected" && <Button size="sm" variant="outline" className="h-7" onClick={() => set("rejected")} data-testid={`showcase-reject-${item.id}`}><X className="w-3 h-3 mr-1" />Reject</Button>}
    </div>
  );
};

export const SpotlightDialog = ({ champ, auth, onDone }) => {
  const [open, setOpen] = useState(false);
  const [story, setStory] = useState(champ.spotlight_story || "");
  const [storyHi, setStoryHi] = useState(champ.spotlight_story_hi || "");
  const [pid, setPid] = useState(champ.spotlight_showcase_id || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/champions/${champ.month}/spotlight`, { story, story_hi: storyHi, showcase_id: pid || null }, auth);
      toast.success(data.auto_translated ? "Saved — Hindi story auto-translated with AI" : "Spotlight saved — the latest one shows on the home page");
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail?.toString?.() || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7" data-testid={`spotlight-edit-${champ.month}`}><Sparkles className="w-3 h-3 mr-1" />{champ.spotlight_story ? "Edit story" : "Write story"}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="spotlight-dialog">
        <DialogHeader>
          <DialogTitle>Spotlight · {champ.name} ({champ.month})</DialogTitle>
          <DialogDescription>This story appears in the Maker of the Month card on the home page. Only the first name and school are shown.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Textarea rows={6} minLength={10} maxLength={1500} value={story} onChange={(e) => setStory(e.target.value)} placeholder="Tell the champion's story… or tap Speak" data-testid="spotlight-story-input" className="pr-24" />
          <div className="absolute right-2 bottom-2"><MicButton auth={auth} testid="spotlight-story-mic" onText={(t) => setStory((p) => appendText(p, t).slice(0, 1500))} /></div>
        </div>
        <label className="text-sm font-semibold text-[#0A192F]">Hindi story <span className="font-normal text-slate-500">(leave empty to auto-translate with AI)</span></label>
        <div className="relative">
          <Textarea rows={5} maxLength={3000} value={storyHi} onChange={(e) => setStoryHi(e.target.value)} placeholder="हिंदी में कहानी… या बोलें" data-testid="spotlight-story-hi-input" className="pr-24" />
          <div className="absolute right-2 bottom-2"><MicButton auth={auth} testid="spotlight-story-hi-mic" onText={(t) => setStoryHi((p) => appendText(p, t).slice(0, 3000))} /></div>
        </div>
        <label className="text-sm font-semibold text-[#0A192F]">Project photo</label>
        <select value={pid} onChange={(e) => setPid(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white text-[#0A192F] px-3 text-sm" data-testid="spotlight-project-select">
          <option value="">Latest approved project (auto)</option>
          {(champ.projects_available || []).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        {(champ.projects_available || []).length === 0 && <p className="text-xs text-amber-700">No approved project photos for this student yet.</p>}
        <Button onClick={save} disabled={busy || story.trim().length < 10} className="rs-cta rounded-full" data-testid="spotlight-save-btn">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />{storyHi.trim() ? "Saving…" : "Translating & saving…"}</> : "Save spotlight"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
